import { useEffect, useRef, useState, type MouseEvent } from 'react';


import { Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface DisplayNameProps {
  name: string;
  className?: string;
  maxWidth?: string;
}

export const DisplayName = ({
  name,
  className,
  maxWidth = '20vw',
}: DisplayNameProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isTrunc = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTruncated(isTrunc);
      }
    };

    // Check immediately
    checkTruncation();

    // Check after a small delay to ensure layout is complete
    const timeoutId = setTimeout(checkTruncation, 0);

    // Re-check on window resize
    window.addEventListener('resize', checkTruncation);

    // Use ResizeObserver to detect container size changes
    let resizeObserver: ResizeObserver | null = null;
    if (textRef.current) {
      resizeObserver = new ResizeObserver(checkTruncation);
      resizeObserver.observe(textRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [name, maxWidth]);

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(name);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const content = (
    <span
      ref={textRef}
      className={cn('block truncate', className)}
      style={maxWidth !== '100%' ? { maxWidth } : undefined}
    >
      {name}
    </span>
  );

  if (!isTruncated) {
    return content;
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-md">
          <div className="flex items-start gap-2">
            <p
              className="flex-1 max-w-[350px] break-words [overflow-wrap:anywhere]"
            >
              {name}
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
              <span className="sr-only">Copy to clipboard</span>
            </Button>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
